import * as React from 'react';
import moment from 'moment';
import { throttle } from 'lodash';

import Input from 'components/inputs/input/Input';
import DatePickerMulti from 'components/date-picker/date-picker-multi/DatePickerMultiLazy';

import { getHTMLDate } from 'utils/date';
import { getQueryValueFromUrl } from 'utils/url';
import { updateQueryParams } from 'utils/location';
import { __t, applyTranslations } from 'utils/language';

import type { CustomReportOffer, LocationWindow, TextEntry } from 'types';

import './report-download-filters.scss';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./report-download-filters.i18n.json'));

type Props = {
  location: LocationWindow,
  groupedDeliverables: Array<CustomReportOffer>,
  startDate?: string | null,
  endDate?: string | null,
  isDisplaySelectedOnly: boolean,
  onChange: (query: string, startDate?: string | null, endDate?: string | null) => void
};

type State = {
  queryString: string,
  stateStartDate?: string | null,
  stateEndDate?: string | null
};

const Query = {
  SEARCH: 'search',
  START_DATE: 'start_date',
  END_DATE: 'end_date'
};

class ReportDownloadFilters extends React.PureComponent<Props, State> {
  state = {
    queryString: getQueryValueFromUrl(this.props.location, Query.SEARCH) ?? '',
    stateStartDate: getQueryValueFromUrl(this.props.location, Query.START_DATE),
    stateEndDate: getQueryValueFromUrl(this.props.location, Query.END_DATE)
  };

  constructor(props: Props) {
    super(props);

    this.throttledUpdate = throttle(this.handleUpdate, 500);
  }

  componentDidMount() {
    this.throttledUpdate();
    this.prefillDates();
  }

  componentDidUpdate(prevProps: Props) {
    const { startDate, endDate, groupedDeliverables } = this.props;
    const newDeliverablesHash = groupedDeliverables.map(_ => _.id).join('|');
    const prevDeliverablesHash = prevProps.groupedDeliverables.map(_ => _.id).join('|');

    const propDateWasChanged = prevProps.startDate !== startDate
      || prevProps.endDate !== endDate
      || prevDeliverablesHash !== newDeliverablesHash;

    if (propDateWasChanged) {
      this.prefillDates();
    }
  }

  prefillDates(): void {
    const { groupedDeliverables, startDate, endDate } = this.props;
    const { stateStartDate, stateEndDate } = this.state;

    const hasCampaignsDate = Boolean(startDate && endDate);
    const hasNoDatesInState = Boolean(!stateStartDate && !stateEndDate);
    const hasGroupedDeliverables = groupedDeliverables.length > 0;

    if (
      hasCampaignsDate
      && hasNoDatesInState
      && hasGroupedDeliverables
    ) {
      let minDate = moment(startDate ?? '').valueOf();
      let maxDate = moment(endDate ?? '').valueOf();

      groupedDeliverables.forEach((group) => {
        group.deliverables.forEach((deliverable) => {
          const deliverableStart = moment(deliverable.startDate).valueOf();
          const deliverableEnd = moment(deliverable.endDate).valueOf();

          minDate = Math.min(minDate, deliverableStart);
          maxDate = Math.max(maxDate, deliverableEnd);
        });
      });

      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        stateStartDate: getHTMLDate(minDate),
        stateEndDate: getHTMLDate(maxDate)
      }, this.throttledUpdate);
    }
  }

  handleUpdate = () => {
    const { location, onChange } = this.props;
    const { queryString, stateStartDate, stateEndDate } = this.state;

    updateQueryParams(location, {
      [Query.SEARCH]: queryString,
      [Query.START_DATE]: stateStartDate ?? '',
      [Query.END_DATE]: stateEndDate ?? ''
    });

    onChange(queryString, stateStartDate, stateEndDate);
  };

  throttledUpdate = () => {};

  handleQueryChange = (event: SyntheticEvent<TextEntry>) => {
    this.setState({
      queryString: event.currentTarget.value
    }, this.throttledUpdate);
  };

  handleTimeframeChange = (newStartDate?: string | null, newEndData?: string | null) => {
    this.setState({
      stateStartDate: newStartDate,
      stateEndDate: newEndData
    }, this.throttledUpdate);
  };

  render(): React.ReactNode {
    const { isDisplaySelectedOnly } = this.props;
    const { queryString, stateStartDate, stateEndDate } = this.state;

    return (
      <div className="report-download-filters">
        <div className="report-download-filters__section">
          <div className="report-download-filters__section-label">
            {__t('report_download_filters.search_by_label')}
          </div>
          <Input
            name="query"
            autoComplete="off"
            value={queryString}
            placeholder={__t('report_download_filters.search_by_placeholder')}
            onChange={this.handleQueryChange}
            disabled={isDisplaySelectedOnly}
          />
        </div>
        <div className="report-download-filters__section">
          <div className="report-download-filters__section-label">
            {__t('report_download_filters.timeframe_label')}
          </div>
          <DatePickerMulti
            noPastDates={false}
            startDate={stateStartDate}
            endDate={stateEndDate}
            onChange={this.handleTimeframeChange}
            isDisabled={isDisplaySelectedOnly}
          />
        </div>
      </div>
    );
  }
}

export default ReportDownloadFilters;
